import { useEffect } from 'react';
import { isLikelyDesktopSiteMode, isStandalonePWA, readSignals } from './desktopSiteMode';

/**
 * FEATURE FLAG — flip to `false` for a one-line kill switch if the scale-transform
 * mitigation below misbehaves on real hardware. This does NOT touch
 * DesktopSiteBanner.tsx, which stays up regardless as the manual fallback (tell the
 * user to turn Chrome's "Desktop site" toggle off themselves), and it does NOT
 * touch useIsMobile()'s plain narrow-viewport check — only the desktop-site-forced
 * detection path is gated. Flip this back to `false`, redeploy, done — no revert of
 * the rest of the PR needed.
 */
export const FORCED_MOBILE_LAYOUT_ENABLED = true;

const DATA_ATTR = 'data-force-mobile';

/**
 * Chrome's per-site "Desktop site" toggle forces the LAYOUT viewport to ~980px and
 * ignores <meta name="viewport">. window.innerWidth then genuinely reports ~980, so
 * plain CSS media queries (which key off innerWidth-derived breakpoints) cannot
 * distinguish "a real 980px-wide desktop window" from "a 412px phone forced wide" —
 * both look identical to a `@media (max-width: Npx)` query. That's already been
 * investigated and confirmed; this hook routes around it with a JS-driven signal
 * (isLikelyDesktopSiteMode, from desktopSiteMode.ts) instead of a CSS one.
 *
 * TECHNIQUE CHOICE (Option A: scale-transform on the #root container) — ResponseKit's
 * #root is a single flex column (see globals.css: `#root { height: 100%; display:
 * flex; flex-direction: column; }`) with no nested layout roots, so a single
 * `transform: scale()` + compensating width/height on that one element cleanly
 * re-maps the whole desktop-width render down into the phone's real pixel footprint.
 * This was the deciding factor over the rejected alternative:
 *
 * REJECTED — Option B (repoint the app's own `@media (max-width: Npx)` breakpoints to
 * `html[data-force-mobile="true"] .foo` attribute-selector overrides): the app's
 * mobile/desktop split is NOT centralized. globals.css alone has breakpoints at
 * 600px, 859/860px, and 1200-1500px; schedule.css has its own separate set; and five
 * page components (CallHistory, AdminInbox, AuditLog, Diversion, Members) ALSO branch
 * layout in JS off useIsMobile()'s own 600px breakpoint — a different number again.
 * Re-pointing three-plus scattered breakpoint sets across CSS AND JS is a much larger,
 * riskier surface than one scale-transform, and was out of scope for the time
 * available. Flagged here rather than attempted partially. (useIsMobile() itself IS
 * now desktop-site-aware — see useIsMobile.ts — so those 5 JS-branching consumers get
 * correct behavior "for free" without any Option-B CSS rework; this hook only handles
 * the remaining raw-CSS-pixel layout that useIsMobile() doesn't reach.)
 *
 * The scale factor is `window.screen.width / window.innerWidth` — i.e. "shrink the
 * desktop-width render back down to the phone's real physical pixel width." This
 * directly depends on window.screen.width reporting the PHYSICAL device width while
 * desktop-site mode is on, which desktopSiteMode.ts's own doc comment flags as
 * ⚠️ UNVERIFIED ON REAL HARDWARE (headless environments can't reproduce Chrome's
 * desktop-site behavior). If screen.width looks untrustworthy — 0, or not smaller
 * than innerWidth — we deliberately SKIP the transform rather than scale by a bogus
 * or inverted factor; the data-force-mobile attribute (for any Option-B-style CSS
 * hooks added later) and the DesktopSiteBanner manual-fix path both still apply.
 *
 * NOT in scope / structurally impossible from page JS (noted so it's clear this was
 * considered, not missed): this cannot and does not attempt to affect Chrome's own
 * browser chrome (tabs, address bar, the "Desktop site" menu item itself) — page
 * script has no access to browser UI. It only affects the app's OWN rendered content
 * inside the page.
 *
 * Real-desktop safety: isLikelyDesktopSiteMode() already requires touch-primary
 * hardware alongside the wide viewport (see desktopSiteMode.ts), so a genuine
 * desktop browser at normal width is never flagged and this hook is a no-op there.
 * That guard is NOT weakened or duplicated here — reused as-is.
 *
 * STANDALONE-PWA SCOPING: Brandon explicitly requested that this override NEVER
 * apply in a regular browser tab — only inside an installed/standalone PWA session,
 * even when isLikelyDesktopSiteMode() also fires in a browser tab (e.g. someone
 * browsing the site directly in Chrome with "Desktop site" enabled on their phone,
 * without having installed the app). Applying a page-wide scale-transform outside
 * the installed-app context is a bigger, less-expected behavior change than this
 * mitigation is meant to make, so isStandalonePWA() (desktopSiteMode.ts) gates the
 * whole mechanism alongside FORCED_MOBILE_LAYOUT_ENABLED and isLikelyDesktopSiteMode().
 * All three must be true for the data-force-mobile attribute or the scale-transform
 * to apply.
 */
function applyForcedMobileScale(forced: boolean): void {
  const root = document.documentElement;

  if (!forced || !FORCED_MOBILE_LAYOUT_ENABLED) {
    root.removeAttribute(DATA_ATTR);
    clearScale();
    return;
  }

  root.setAttribute(DATA_ATTR, 'true');

  const innerWidth = window.innerWidth;
  const screenWidth = window.screen?.width ?? 0;

  // Untrustworthy signal per desktopSiteMode.ts's own UNVERIFIED caveat: bail out
  // of the transform (data-force-mobile stays set; banner fallback still applies)
  // rather than scale by a zero, negative, or inverted factor.
  if (screenWidth <= 0 || screenWidth >= innerWidth) {
    clearScale();
    return;
  }

  const appRoot = document.getElementById('root');
  if (!appRoot) return;

  const scale = screenWidth / innerWidth;
  appRoot.style.transformOrigin = 'top left';
  appRoot.style.transform = `scale(${scale})`;
  // Compensate the box size so the scaled-down element still fills the real
  // viewport instead of leaving a scale-shrunk gap (transform affects paint only,
  // not layout flow/box size).
  appRoot.style.width = `${100 / scale}%`;
  appRoot.style.height = `${100 / scale}%`;
}

function clearScale(): void {
  const appRoot = document.getElementById('root');
  if (!appRoot) return;
  appRoot.style.transform = '';
  appRoot.style.transformOrigin = '';
  appRoot.style.width = '';
  appRoot.style.height = '';
}

/**
 * Mount ONCE near the app root (see App.tsx). Owns the data-force-mobile attribute
 * + scale-transform DOM side effect for as long as it's mounted; re-evaluates on
 * resize/orientationchange with the same 200ms debounce as DesktopSiteBanner.tsx so
 * the two mechanisms track the same signal at the same cadence.
 *
 * This hook does not return anything for layout branching — use useIsMobile() (in
 * useIsMobile.ts) for that; it's already desktop-site-aware and is what the app's
 * existing page components consume.
 */
export function useForcedMobileLayout(): void {
  useEffect(() => {
    if (!FORCED_MOBILE_LAYOUT_ENABLED) return;

    // Standalone-PWA gate: see the STANDALONE-PWA SCOPING doc comment above. Checked
    // per-evaluate (not hoisted out of the closure) so it stays correct if display
    // mode changes mid-session (e.g. iOS Safari's `navigator.standalone` can flip
    // after an install prompt is accepted without a full page reload on some flows).
    const evaluate = () =>
      applyForcedMobileScale(isStandalonePWA() && isLikelyDesktopSiteMode(readSignals()));
    evaluate();

    let t: number | undefined;
    const onChange = () => {
      window.clearTimeout(t);
      t = window.setTimeout(evaluate, 200);
    };
    window.addEventListener('resize', onChange);
    window.addEventListener('orientationchange', onChange);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', onChange);
      window.removeEventListener('orientationchange', onChange);
      applyForcedMobileScale(false);
    };
  }, []);
}

// Exported for tests only.
export const __test__ = { applyForcedMobileScale, clearScale, DATA_ATTR };
